import React from "react";
import { Navbar } from "../../../Components/Navbar/Navbar";
import { Footer } from "../../../Components/Footer/footer";
import { db } from "../../../firebase";
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  serverTimestamp,
  deleteDoc,
  orderBy,
  query,
} from "firebase/firestore";
import "./admin-institutes.css";

type Institute = { id: string; name: string };

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function AdminInstitutesPage() {
  const [file, setFile] = React.useState<File | null>(null);
  const [parsed, setParsed] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [list, setList] = React.useState<Institute[]>([]);
  const [msg, setMsg] = React.useState<string>("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const col = collection(db, "institutes");
      const snap = await getDocs(query(col, orderBy("name")));
      setList(
        snap.docs.map((d) => ({ id: d.id, name: (d.data() as any).name })) as Institute[]
      );
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // very small CSV parser: supports either 'name' header or single-column CSV
  const parseCSV = async (f: File) => {
    const text = await f.text();
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length === 0) {
      setParsed([]);
      setMsg("Fișierul e gol.");
      return;
    }

    let names: string[] = [];

    // if first row includes 'name' assume header
    const first = lines[0].toLowerCase();
    const hasHeader = first.split(",").map((s) => s.trim()).includes("name");

    if (hasHeader) {
      const header = lines[0].split(",").map((s) => s.trim().toLowerCase());
      const nameIdx = header.indexOf("name");
      if (nameIdx === -1) {
        setParsed([]);
        setMsg("Nu am găsit coloana 'name' în header.");
        return;
      }
      names = lines.slice(1).map((row) => row.split(",")[nameIdx]?.trim() || "");
    } else {
      // treat each full line as the name (or first column if commas exist)
      names = lines.map((row) => row.split(",")[0]?.trim() || "");
    }

    // clean/unique
    const cleaned = Array.from(
      new Set(
        names
          .map((n) => n.replace(/\s+/g, " ").trim())
          .filter((n) => n.length > 1)
      )
    );

    setParsed(cleaned);
    setMsg(`Am găsit ${cleaned.length} denumiri.`);
  };

  const importToFirestore = async () => {
    if (parsed.length === 0) {
      setMsg("Nu există rânduri de importat.");
      return;
    }
    setSaving(true);
    setMsg("");
    try {
      const batch = writeBatch(db);
      const col = collection(db, "institutes");

      parsed.forEach((name) => {
        const id = slugify(name) || crypto.randomUUID();
        const ref = doc(col, id);
        batch.set(
          ref,
          { name, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
          { merge: true }
        );
      });
      await batch.commit();
      setMsg("✔️ Import finalizat.");
      setParsed([]);
      setFile(null);
      await load();
    } catch (e: any) {
      console.error(e);
      setMsg("❌ Eroare la import.");
    } finally {
      setSaving(false);
    }
  };

  const deleteOne = async (id: string) => {
    if (!window.confirm("Ștergi această instituție?")) return;
    await deleteDoc(doc(db, "institutes", id));
    await load();
  };

  const deleteAll = async () => {
    if (
      !window.confirm("⚠️ Ștergi TOATE instituțiile? Operațiune ireversibilă.")
    )
      return;
    setSaving(true);
    try {
      const col = collection(db, "institutes");
      const snap = await getDocs(col);
      const batch = writeBatch(db);
      snap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      setMsg("✔️ Toate instituțiile au fost șterse.");
      await load();
    } catch (e) {
      console.error(e);
      setMsg("❌ Eroare la ștergerea tuturor.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <Navbar />
      <div className="page-content admin-institutes">
        <h1>Admin – Instituții (CSV)</h1>

        <section className="panel">
          <h3>1) Încarcă CSV</h3>
          <p className="hint">
            Acceptă fie o singură coloană cu numele, fie un header <code>name</code>.
          </p>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              setFile(f);
              setParsed([]);
              setMsg("");
              if (f) parseCSV(f);
            }}
          />
          {parsed.length > 0 && (
            <>
              <div className="preview">
                <strong>Preview ({parsed.length}):</strong>
                <ul>
                  {parsed.slice(0, 200).map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
                {parsed.length > 200 && <em>…și restul</em>}
              </div>
              <button
                className="primary"
                disabled={saving}
                onClick={importToFirestore}
              >
                {saving ? "Se importă…" : "Importă în Firestore"}
              </button>
            </>
          )}
          {msg && <p className="msg">{msg}</p>}
        </section>

        <section className="panel">
          <h3>2) Lista curentă</h3>
          {loading ? (
            <p>Se încarcă…</p>
          ) : list.length === 0 ? (
            <p>— nici o instituție —</p>
          ) : (
            <>
              <div className="table">
                {list.map((it) => (
                  <div key={it.id} className="row">
                    <span>{it.name}</span>
                    <button className="danger" onClick={() => deleteOne(it.id)}>
                      Șterge
                    </button>
                  </div>
                ))}
              </div>
              <button
                className="danger outline"
                disabled={saving}
                onClick={deleteAll}
              >
                Șterge toate
              </button>
            </>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
}